import React, { createContext, useContext, useState } from 'react';

import UserFeedbackService from '@qality/cloud-services/src/UserFeedbackService';
import { Feedback } from '@qality/common-model/src/Feedback';

type FeedbackContextValue = {
  saveFeedbackRating: (data: Pick<Feedback, 'rating'>) => void;
  saveFeedbackComment: (data: Pick<Feedback, 'comment'>) => Promise<void>;
};

export const FeedbackContext = createContext<FeedbackContextValue>({
  saveFeedbackRating: () => undefined,
  saveFeedbackComment: () => undefined,
});

export const useFeedback = (): FeedbackContextValue => {
  return useContext(FeedbackContext);
};

type Props = {
  children: React.ReactNode;
};

export default function FeedbackProvider({ children }: Props): JSX.Element {
  const [saveFeedbackPromise, setSaveFeedbackPromise] =
    useState<ReturnType<typeof UserFeedbackService.saveRating>>(null);

  const saveFeedbackRating: FeedbackContextValue['saveFeedbackRating'] = (
    data
  ) => {
    try {
      const newSaveFeedbackPromise = UserFeedbackService.saveRating(data);
      setSaveFeedbackPromise(newSaveFeedbackPromise);
    } catch {
      // Ignore
      // In case of 'bad emoji' this request failure will cause
      // the 'saveComment' request failure
    }
  };

  const saveFeedbackComment: FeedbackContextValue['saveFeedbackComment'] =
    async (data) => {
      try {
        const saveFeedbackResponse = await saveFeedbackPromise;

        await UserFeedbackService.saveComment({
          id: saveFeedbackResponse.data,
          comment: data.comment,
        });
      } catch {
        // Ignore
      }
    };

  return (
    <FeedbackContext.Provider
      value={{ saveFeedbackRating, saveFeedbackComment }}
    >
      {children}
    </FeedbackContext.Provider>
  );
}
